import { useEffect, useRef, useState } from 'react';

import { styled, css } from 'styled-components';
import { useMsal } from '@azure/msal-react';

import { Avatar, Box, Button, HorizontalRule, Popover, Stack, Text } from '@ssnc/bsds-core';
import { DoorExit } from '@ssnc/bsds-icons';

import { getAppConfig } from '../../lib/config';

interface ProfileMenuViewProps {
  name: string;
  email?: string;
  /** Omitted when there is no session to end (demo mode); the button renders disabled. */
  onSignOut?: () => void;
}

// Fixed size rather than Avatar's default `100%`, which would stretch to fill the header cell.
const AvatarSlot = styled.div(
  ({ theme }) => css`
    width: ${theme.space[500]};
    height: ${theme.space[500]};
  `,
);

// Popover sizes itself to `min-content`, which would squeeze the name and button to a sliver.
const MenuPanel = styled.div`
  min-width: 14rem;
`;

/**
 * bsds-core 0.8.0 has no Menu component, so this is an Avatar button plus a Popover. Popover
 * already closes itself on outside click, scroll and resize; Escape is handled here.
 */
export function ProfileMenuView({ name, email, onSignOut }: ProfileMenuViewProps) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        anchorRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  return (
    <>
      <AvatarSlot>
        <Avatar
          ref={anchorRef}
          name={name}
          ariaLabel={`Account menu for ${name}`}
          isToggleButton
          active={open}
          onClick={() => setOpen((wasOpen) => !wasOpen)}
        />
      </AvatarSlot>
      <Popover anchorRef={anchorRef} visible={open} placement="bottom" maxWidth="20rem" onClose={() => setOpen(false)}>
        <MenuPanel>
          <Box padding={300}>
            <Stack gap={200}>
              <Stack gap={50}>
                <Text type="body" styleAs="uiEmphasised" truncated>
                  {name}
                </Text>
                {email && (
                  <Text type="body" styleAs="uiSmall" themeColor="subtle" truncated>
                    {email}
                  </Text>
                )}
              </Stack>
              <HorizontalRule />
              <Button importance="secondary" fullWidth disabled={!onSignOut} onClick={onSignOut}>
                <DoorExit />
                Sign out
              </Button>
            </Stack>
          </Box>
        </MenuPanel>
      </Popover>
    </>
  );
}

function MsalProfileMenu() {
  const { instance, accounts } = useMsal();
  const account = instance.getActiveAccount() ?? accounts[0];

  if (!account) {
    return null;
  }

  return (
    <ProfileMenuView
      name={account.name || account.username}
      email={account.username}
      onSignOut={() => {
        instance.logoutRedirect({ account, postLogoutRedirectUri: window.location.origin });
      }}
    />
  );
}

/** Must render inside AuthGate: outside demo mode it relies on AuthGate's MsalProvider. */
export function ProfileMenu() {
  if (getAppConfig().demoMode) {
    return <ProfileMenuView name="Demo user" />;
  }

  return <MsalProfileMenu />;
}
